/**
 * Penelope's one settings editor (R9), used by the ticket, the /live popover and the Desk drawer. Additions only — the
 * knob groups keep their names and order, and the two tiers are PenelopeSettings' own:
 *
 *   strategy bar   the switches that sat in closed Advanced groups (Puppy Love, split book, sell back while off, the
 *                  game kill), plus the row's Start/Stop ("Value") and the mini game's own row
 *   search         label, key, hint, group title or an alias ("clip", "max net", "contracts per side")
 *   changed (N)    only the knobs set differently from what one-click Start would run, each with its default and reset
 *   basic          the eight basic knobs, then `between` (the caller's controls: side switches, Run, Save as default)
 *   Advanced       every other knob behind the disclosure a manager can switch off; a search or the changed filter
 *                  opens it on what it found
 *
 * Writes go through `save` exactly as a knob edit does; the bar's switches are knob edits too (or the row's own arm /
 * disarm), and on a LIVE row each asks first.
 *
 * StreamRift mode (`sr`, 2026-10-20): a navigation layer over the same tree — a sticky settings map at the top of
 * Advanced that jumps to any group, a ⚙ beside each strategy chip that opens that strategy's group, and a per-surface
 * memory of which groups (and whether Advanced) were open. Nothing moves, nothing is renamed; with `sr` off the editor
 * renders exactly as before.
 */
import { useState, type ReactNode } from 'react';
import { Settings2 } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import type { PenelopeConfig } from '@/hooks/usePenelope';
import { KNOB_GROUPS, PENELOPE_GROUP_CLIPBOARD, PENELOPE_GROUP_VARIANTS, STRATEGY_SWITCHES, changedByGroup, groupDomId, shortGroupTitle, type KnobStrategy, type StrategySwitch } from './knobs';
import { changedKeys } from './baseline';
import { EMPTY_EDITOR_MEMORY, defaultOpenGroups, readEditorMemory, writeEditorMemory, type EditorMemory, type PenelopeSurface } from './editorMemory';
import { PenelopeSettings, visibleKnobs, type PenelopeKnobBounds } from './PenelopeSettings';
import { PenelopeSettingsMap, type MapEntry } from './PenelopeSettingsMap';

const ALL_KEYS = KNOB_GROUPS.flatMap((g) => g.knobs.map((k) => k.key));
const chip = 'rounded border px-1.5 py-px font-mono text-[10px] disabled:opacity-50';

export interface PenelopeEditorProps {
  cfg: PenelopeConfig;
  save: (patch: Partial<PenelopeConfig>) => void;
  busy?: boolean;
  editable: boolean;
  teamA: string;
  teamB: string;
  strategy?: KnobStrategy;
  /** The member may open Advanced (a manager has not switched it off). */
  advanced: boolean;
  bounds?: PenelopeKnobBounds;
  fieldErrors?: Array<{ field: string; error: string }>;
  /** What one-click Start would run (knobBaseline): the "changed from default" base. Null hides the changed filter. */
  base?: Partial<PenelopeConfig> | null;
  /** The row's Start/Stop, for the bar's "Value" switch; absent before a row exists. */
  row?: { armed: boolean; setArm: (on: boolean) => void } | null;
  /** The game's mini game row, for the bar's "Mini game" switch; with no row yet it opens the panel instead. */
  mini?: { exists: boolean; armed: boolean; live: boolean; setArm: (on: boolean) => void; open: () => void } | null;
  /** A LIVE row: the bar's switches ask first. */
  live?: boolean;
  /** Rendered between the basic knobs and Advanced (side switches, Run, Save as my default). */
  between?: ReactNode;
  /** Rendered at the top / bottom of Advanced (presets / kill, the variants readout). */
  advancedHead?: ReactNode;
  advancedFoot?: ReactNode;
  /** StreamRift mode (2026-10-20): the settings map, the ⚙ beside each strategy chip and the per-surface memory of
   *  what was open. Off (default) = today's editor. */
  sr?: boolean;
  /** Which surface's memory to read and write (StreamRift mode). */
  surface?: PenelopeSurface;
  /** The map's shape: chips where there is room, one select in the 26 rem popover and the ticket (StreamRift mode). */
  mapDensity?: 'chips' | 'select';
}

export function PenelopeEditor(p: PenelopeEditorProps) {
  const { cfg, save, busy = false, editable, teamA, teamB, strategy = 'penelope', advanced, bounds, fieldErrors, base = null, sr = false, surface = 'ticket', mapDensity = 'chips' } = p;
  const [query, setQuery] = useState('');
  const [onlyChanged, setOnlyChanged] = useState(false);
  // StreamRift mode: what this surface last had open, read once on mount (never in an effect); written by the handlers.
  const [mem, setMem] = useState<EditorMemory>(() => (sr ? readEditorMemory(surface, strategy) : EMPTY_EDITOR_MEMORY));
  const remember = (m: EditorMemory) => { setMem(m); writeEditorMemory(surface, m, strategy); };
  const filtering = query.trim() !== '' || onlyChanged;
  const changedN = base ? changedKeys(cfg, base, ALL_KEYS).length : 0;
  const advancedKnobs = visibleKnobs({ tier: 'advanced-only', strategy, query, onlyChanged, cfg, base });
  const basicKnobs = visibleKnobs({ tier: 'basic', strategy, query, onlyChanged, cfg, base });
  const advancedHits = advancedKnobs.reduce((n, g) => n + g.knobs.length, 0);
  const hits = advancedHits + basicKnobs.reduce((n, g) => n + g.knobs.length, 0);
  const shared = { cfg, editable, busy, save, teamA, teamB, bounds, fieldErrors, strategy, query, onlyChanged, base };

  // StreamRift mode, derived: the open groups (remembered, else today's first two), whether Advanced is open (a search
  // or the changed filter opens it on what it found, as today; otherwise what was remembered, else closed), the counts.
  const openGroups = new Set(mem.groups ?? defaultOpenGroups(strategy));
  const advancedOpen = filtering ? advancedHits > 0 : (mem.advanced ?? (mem.groups !== null && mem.groups.length > 0));
  const counts = sr ? changedByGroup(cfg, base, strategy) : new Map<string, number>();
  const entries: MapEntry[] = sr
    ? [
      ...advancedKnobs.map((g) => ({ id: groupDomId(g.title), title: g.title, short: shortGroupTitle(g.title), changed: counts.get(g.title) ?? 0, hits: filtering ? g.knobs.length : null, open: filtering || openGroups.has(g.title) })),
      ...(filtering ? [] : [
        { id: PENELOPE_GROUP_VARIANTS, title: 'variants', short: 'variants', changed: 0, hits: null, open: false, tail: true },
        { id: PENELOPE_GROUP_CLIPBOARD, title: 'copy / paste JSON', short: 'copy / paste JSON', changed: 0, hits: null, open: false, tail: true },
      ]),
    ]
    : [];
  /** Open Advanced and (for a group) only that group, remember it, and scroll there once the groups have re-rendered
   *  (a closed <details> has no box to scroll to until then). */
  const jumpTo = (id: string, title?: string) => {
    remember({ advanced: true, groups: title ? [title] : mem.groups });
    setTimeout(() => document.getElementById(id)?.scrollIntoView?.({ block: 'start' }), 0);
  };
  const toggleGroup = (title: string, open: boolean) => {
    const was = mem.groups ?? defaultOpenGroups(strategy);
    remember({ ...mem, groups: open ? [...was.filter((t) => t !== title), title] : was.filter((t) => t !== title) });
  };
  const jump = sr && advanced ? (group: string) => jumpTo(groupDomId(group), group) : undefined;

  return (
    <div className="flex flex-col gap-2" data-testid="penelope-editor">
      <StrategyBar {...p} busy={busy} strategy={strategy} jump={jump} />
      <div className="flex flex-wrap items-center gap-2 text-[10px]">
        <input
          type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="find a setting…" aria-label="find a setting"
          className="border-border bg-background min-w-0 flex-1 rounded border px-1.5 py-0.5 font-mono text-[11px]"
        />
        {base && (
          <button type="button" aria-pressed={onlyChanged} onClick={() => setOnlyChanged((x) => !x)} disabled={changedN === 0 && !onlyChanged}
            className={cn(chip, onlyChanged ? 'border-sky-500 text-sky-500' : 'border-border/60 text-muted-foreground hover:text-foreground')}
            title="Only the settings that differ from what one-click Start would run here (your saved default for this sport, else your all-sports default, else the box's)">
            changed ({changedN})
          </button>
        )}
      </div>
      {filtering && hits === 0 && <p className="text-muted-foreground text-[10px]">{onlyChanged && !query.trim() ? 'Nothing differs from your default.' : `No setting matches “${query.trim()}”.`}</p>}
      <PenelopeSettings {...shared} tier="basic" />
      {p.between}
      {advanced ? (
        <details
          key={filtering ? 'found' : 'closed'}
          open={sr ? advancedOpen : (filtering && advancedHits > 0 ? true : undefined)}
          // The browser toggles the attribute before the event: only a toggle that moved it off the prop is the trader's.
          onToggle={sr ? (e) => { if (!filtering && e.currentTarget.open !== advancedOpen) remember({ ...mem, advanced: e.currentTarget.open }); } : undefined}
          className="border-border/60 border-t pt-1"
        >
          <summary className="text-muted-foreground cursor-pointer text-[10px] uppercase tracking-wide">
            Advanced settings — every knob, variants, kill{filtering ? ` (${advancedHits} found)` : ''}
          </summary>
          <div className="mt-1 flex flex-col gap-2">
            {sr && <PenelopeSettingsMap entries={entries} density={mapDensity} filtering={filtering} onJump={(e) => jumpTo(e.id, e.tail ? undefined : e.title)} />}
            {!filtering && p.advancedHead}
            {sr
              ? <PenelopeSettings {...shared} tier="advanced-only" openGroups={openGroups} onToggleGroup={toggleGroup} changedByGroup={counts} />
              : <PenelopeSettings {...shared} tier="advanced-only" />}
            {!filtering && p.advancedFoot}
          </div>
        </details>
      ) : (
        <p className="text-muted-foreground text-[10px]">
          Other settings use the desk's defaults.{filtering && advancedHits > 0 ? ` ${advancedHits} more match in Advanced — a manager can switch it on for you.` : ''}
        </p>
      )}
    </div>
  );
}

/** The bar: a chip per switch, its state on its face. Advanced switches need the Advanced tier: without it they read
 *  as locked chips. StreamRift mode adds a ⚙ beside each chip whose switch has a group, jumping to that group. */
function StrategyBar(p: PenelopeEditorProps & { busy: boolean; strategy: KnobStrategy; jump?: (group: string) => void }) {
  const { cfg, save, editable, busy, advanced, live, row, mini, strategy, base, bounds, jump } = p;
  const ask = (what: string, on: boolean): boolean => !live || window.confirm(`${on ? 'Turn on' : 'Turn off'} ${what} on this LIVE game? Real orders follow the change at once.`);
  const items: ReactNode[] = [];
  const penelopeRow = strategy === 'penelope';
  for (const sw of STRATEGY_SWITCHES) {
    const locked = sw.advanced && !advanced;
    const can = editable && !busy && !locked;
    const title = locked ? `${sw.title} — needs Advanced (a manager can switch it on for you)` : sw.title;
    const gear = (label: string) => (jump && sw.group && !locked ? (
      <button type="button" aria-label={`${label} settings`} title={`open ${sw.group}`} onClick={() => jump(sw.group!)} className="text-muted-foreground hover:text-foreground">
        <Settings2 className="h-3 w-3" />
      </button>
    ) : null);
    const toggle = (on: boolean, onClick: () => void, extra?: ReactNode, label = sw.label) => (
      <span key={sw.id} className="flex items-center gap-1">
        <button type="button" aria-pressed={on} disabled={!can} onClick={onClick} title={title} data-switch={sw.id}
          className={cn(chip, on ? 'border-sky-500 bg-sky-500/10 text-sky-500' : 'border-border/60 text-muted-foreground hover:text-foreground', locked && 'cursor-not-allowed')}>
          {locked ? '🔒 ' : ''}{label}{on ? ' on' : ' off'}
        </button>
        {extra}
        {gear(label)}
      </span>
    );
    switch (sw.id) {
      case 'value':
        if (row) items.push(toggle(row.armed, () => row.setArm(!row.armed), undefined, strategy === 'penelope' ? 'Value' : strategy === 'minigame' ? 'Mini game' : 'Harvester'));
        break;
      case 'puppy':
        if (penelopeRow) {
          const on = cfg.offLinePolicy === 'puppy';
          // Off goes back to what the default runs off-line (follow when the default is Puppy Love too).
          const back = base?.offLinePolicy && base.offLinePolicy !== 'puppy' ? base.offLinePolicy : 'follow';
          items.push(toggle(on, () => ask('Puppy Love', !on) && save({ offLinePolicy: on ? back : 'puppy' })));
        }
        break;
      case 'split':
        if (penelopeRow) {
          const on = cfg.splitBookEnabled === true;
          items.push(toggle(on, () => ask('the split book', !on) && save({ splitBookEnabled: !on })));
        }
        break;
      case 'mini':
        if (penelopeRow && mini) {
          const on = mini.exists && mini.armed;
          items.push(toggle(on, () => {
            if (!mini.exists) return mini.open();
            if (!on && mini.live && !window.confirm('Turn the mini game on LIVE? Real orders on Kalshi.')) return;
            mini.setArm(!on);
          }));
        }
        break;
      case 'sellBackWhileOff':
        if (penelopeRow) {
          const on = cfg.sellBackWhileOff !== false;
          const hold = cfg.sellBackHoldWhileOff === true;
          items.push(toggle(on, () => ask('sell back while off', !on) && save({ sellBackWhileOff: !on }), !on && (
            <button type="button" aria-pressed={hold} disabled={!can} data-switch="sellBackHoldWhileOff"
              onClick={() => ask('keep a resting sell-back while off', !hold) && save({ sellBackHoldWhileOff: !hold })}
              title="With sell back while off Off: a sell-back already resting when Pinnacle drops stays as it is"
              className={cn(chip, hold ? 'border-sky-500 text-sky-500' : 'border-border/60 text-muted-foreground hover:text-foreground')}>
              keep resting {hold ? 'on' : 'off'}
            </button>
          )));
        }
        break;
      case 'gameKill': {
        const kill = <GameKill key={sw.id} sw={sw} cfg={cfg} save={save} disabled={!can} locked={locked} bound={bounds?.gameKillUsd} live={live === true} />;
        const g = gear(sw.label);
        items.push(g ? <span key={sw.id} className="flex items-center gap-1">{kill}{g}</span> : kill);
        break;
      }
    }
  }
  if (items.length === 0) return null;
  return <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="strategies on this game">{items}</div>;
}

/** The game kill as an inline dollar amount (it has no Off): saved on blur, within this box's bounds. */
function GameKill({ sw, cfg, save, disabled, locked, bound, live }: { sw: StrategySwitch; cfg: PenelopeConfig; save: (patch: Partial<PenelopeConfig>) => void; disabled: boolean; locked: boolean; bound?: { min: number; max: number }; live: boolean }) {
  const v = typeof cfg.gameKillUsd === 'number' ? cfg.gameKillUsd : null;
  const [problem, setProblem] = useState<string | null>(null);
  const min = bound?.min ?? 1;
  const max = bound?.max ?? 5_000;
  return (
    <label className="text-muted-foreground flex items-center gap-1 font-mono text-[10px]" title={locked ? `${sw.title} — needs Advanced` : sw.title}>
      {locked ? '🔒 ' : ''}{sw.label} $
      <input
        type="number" aria-label="game kill in dollars" disabled={disabled} min={min} max={max} step={5}
        defaultValue={v ?? ''} key={String(v)} placeholder={v === null ? '?' : undefined}
        aria-invalid={problem ? true : undefined}
        onBlur={(e) => {
          const next = Number(e.target.value);
          if (e.target.value === '' || next === v) { setProblem(null); return; }
          if (!Number.isFinite(next) || next < min || next > max) { setProblem(`${min}–${max} on this box`); return; }
          setProblem(null);
          // On a LIVE row it asks first, like every switch on this bar (R9 review).
          if (live && !window.confirm(`Set the game kill to $${next} on this LIVE game? She stops this game at −$${next}.`)) { e.target.value = v === null ? '' : String(v); return; }
          save({ gameKillUsd: next });
        }}
        className={cn('border-border bg-background w-16 rounded border px-1 py-px text-right', problem && 'border-red-500')}
      />
      {problem && <span role="alert" className="text-red-500">{problem}</span>}
    </label>
  );
}
